import { useQuery } from "@apollo/client";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { GET_PARTICIPANT_CASE_CHAT } from "../../../apis/queries/getParticipantCase";
import { useParticipantChatWebappFlag } from "../../../utils/launchdarkly/featureFlags";
import { chatConfig } from "../../../config";
import { Case } from "../../../apis/API";

export interface ChatEnabledState {
  /** True when the flag is on, a chat channel is provisioned, and an endpoint is configured. */
  enabled: boolean;
  /** True while the gating case query is in flight (only when the flag is on). */
  loading: boolean;
  /**
   * True when the gating query failed without data. Distinct from
   * enabled=false: an error means "we don't know", so callers must hold or
   * show a retryable error rather than treating chat as not provisioned
   * (same convention as ProtectedRoute's loadCaseError handling).
   */
  error: boolean;
  conversationId?: string;
  chatUserId?: string;
}

/**
 * Gate for the chat feature: LaunchDarkly flag AND the case has a provisioned
 * chat channel AND a chat endpoint is configured for this environment (fail
 * closed). The chat identity fields live in their own flag-gated query so the
 * app-wide boot query stays valid in environments without the schema change.
 */
export function useChatEnabled(): ChatEnabledState {
  const flagOn = useParticipantChatWebappFlag();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const configured = Boolean(chatConfig.endpoint);

  const { data, loading, error } = useQuery<{
    getParticipantCase?: Case | null;
  }>(GET_PARTICIPANT_CASE_CHAT, {
    skip: !flagOn || !configured || authStatus !== "authenticated",
  });

  const participantCase = data?.getParticipantCase;
  const conversationId = participantCase?.chatChannelId ?? undefined;
  const chatUserId = participantCase?.chatUserId ?? undefined;

  return {
    enabled:
      flagOn && configured && Boolean(conversationId) && Boolean(chatUserId),
    loading: flagOn && configured && loading,
    error: Boolean(error) && !data,
    conversationId,
    chatUserId,
  };
}
